import React, { useEffect, useState, useCallback } from "react"
import axios from 'axios'
import { Card, Col, Row, Breadcrumb, Collapse, message, Space, Tabs, Table, Typography, Spin, Input } from "antd"
import { CaretRightOutlined } from '@ant-design/icons'
import { startCase } from "lodash"

import DragUpload from "../Form/DragUpload"
import { getTopupPackage } from '../../utils/topupPackage'

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Text } = Typography;

const columnTypes = {
  prices: ["region", "state", "minWeight", "maxWeight", "dhl", "citylink", "jnt", "poslaju", "ninjavan", "teleport", "best", "lineclear", "spx", "kex", "lex"],
  international: ["region", "state", "minWeight", "maxWeight", "teleport", "sfeconomy", "sfexd", "ninjavan", "aramex", "redly"],
  cargo: ["region", "state", "minWeight", "maxWeight", "bestcargo"],
};

const auditInfoLabel = {
  updatedBy: "Last Updated By",
  updatedAt: "Last Updated At",
  reason: "Reason"
}

const TopupPackagePricing = () => {
  const { permissions } = usePermissions();
  const routes = [
    { path: "", breadcrumbName: "App" },
    { path: "/topup-package/pricing", breadcrumbName: "Topup Package Pricing" },
  ];

  const [isLoading, setIsLoading] = useState(false)
  const [availablePackages, setAvailablePackages] = useState([])
  const [packagePricings, setPackagePricings] = useState({})
  const [activeTab, setActiveTab] = useState(null)
  const [reason, setReason] = useState("");
  const [isAuditLoading, setIsAuditLoading] = useState(false)
  const [auditInfo, setAuditInfo] = useState(null)

  const fetchPackagePricings = useCallback(async () => {
    if (!activeTab || packagePricings[activeTab]) {
      return
    }

    setIsLoading(true)
    const res = await axios.get(`/api/packages/pricings`, { params: { package: activeTab } })

    setPackagePricings(prevPackagePricings => ({
      ...prevPackagePricings,
      [activeTab]: res.data.data
    }))

    setIsLoading(false)
  }, [activeTab])

  const fetchLatestPricingAudit = useCallback(async () => {
    setIsAuditLoading(true)
    try {
      const res = await axios.get("/api/admin-audit-logs", {
        params: {
          subjectType: "Pricing",
          auditType: "ADMIN",
          limit: 1,
        },
      })
      const auditLogs = res.data.data || []
      if (auditLogs.length > 0) {
        const latestAudit = auditLogs[0]
        setAuditInfo({
          updatedBy: latestAudit.updatedBy?.email || latestAudit.updatedBy?.username || "Unknown",
          updatedAt: new Date(latestAudit.createdAt).toLocaleString(),
          reason: latestAudit.changes?.reason || "No reason provided",
        })
      } else {
        setAuditInfo({
          updatedBy: "N/A",
          updatedAt: "N/A",
          reason: "No pricing updates recorded yet",
        })
      }
    } catch (error) {
      message.error("Failed to fetch pricing audit history")
      console.error(error)
    } finally {
      setIsAuditLoading(false)
    }
  }, [])

  const handleUploadSuccess = () => {
    setReason("");
    setPackagePricings({})
    setActiveTab("normal")
    fetchPackagePricings()
    fetchLatestPricingAudit()
  }

  useEffect(() => {
    const getAvailablePackages = async () => {
      const response = await axios.get(`/api/topup-packages`)
      const packages = response.data.meta?.packages || []
      const internationalPackages = response.data.meta?.internationalPackages || []

      setAvailablePackages([...packages, ...internationalPackages])
      setActiveTab("normal")
    }

    getAvailablePackages()
  }, [])
  useEffect(() => { fetchPackagePricings() }, [activeTab, fetchPackagePricings])
  
  useEffect(() => {
    fetchLatestPricingAudit()
  }, [fetchLatestPricingAudit])

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Topup Package Pricing</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "16px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        {permissions?.includes(PERMISSIONS.UPLOAD_PRICING) && (
          <div className="upload">
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header="Upload New Pricing" key="upload">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text strong>Reason for pricing update: </Text>
                    <Row>
                      <Input.TextArea 
                        label="Reason for Pricing Update"
                        name="reason"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Enter reason for pricing update"
                        style={{ marginBottom: 16, width: "100%" }}
                        rows={2}
                      />
                    </Row>
                  </Space>
                  <DragUpload
                    action={`${process.env.REACT_APP_SERVER_URL}/api/packages/pricing`}
                    accept=".xls, .xlsx"
                    method="put"
                    data={{ reason }}
                    beforeUpload={() => {
                      if (!reason.trim()) {
                        message.error("Please enter a reason for the pricing update.");
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    }}
                    onUploadSuccess={handleUploadSuccess}
                  />
                </Space>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        {permissions.includes(PERMISSIONS.VIEW_ADMIN_AUDIT_LOGS) 
          && !isAuditLoading
          && auditInfo 
          && (
          <Card>
            {Object.entries(auditInfoLabel).map(([key, label]) => (
              <Row key={key}>
                <span>
                  <Text strong>{label}:{` `} </Text> {auditInfo[key]}
                </span>
              </Row>
            ))}
          </Card>
        )}

        <div className="table">
          <Tabs activeKey={activeTab} animated onTabClick={setActiveTab}>
            {availablePackages.length && availablePackages.map((type) => {
              const colsKey = type?.startsWith("international")
                ? "international"
                : type?.startsWith("cargo")
                  ? "cargo"
                  : "prices";

              const isInternational = colsKey === "international";
              const isPrices = colsKey === "prices";

              const columns = columnTypes[colsKey].map((columnRaw) => {
                const col = String(columnRaw).toLowerCase();
                return {
                  title: <span>{startCase(columnRaw)}</span>,
                  dataIndex: columnRaw,
                  width: isInternational
                    ? (col === "region" ? 250
                      : ["state", "sfexd", "redly", "aramex", "teleport", "ninjavan"].includes(col) ? 80
                        : 110)
                    : isPrices
                      ? (["ninjavan", "teleport", "lineclear", "region"].includes(col) ? 90
                        : ["citylink", "poslaju", "minweight", "maxweight"].includes(col) ? 80 : 70)
                      : undefined,
                };
              });

              return (
                <Tabs.TabPane tab={getTopupPackage(type).label} key={type}>
                  <Table
                    rowKey="Id"
                    className="table-section"
                    columns={columns}
                    dataSource={packagePricings[type]}
                    loading={isLoading}
                    pagination={{ pageSize: 50 }}
                    scroll={{ x: "max-content", y: isPrices ? 600 : undefined }} // y only where you want it
                    sticky={isPrices ? true : undefined}
                  />
                </Tabs.TabPane>
              );
            })}
          </Tabs>


          {!availablePackages.length && <Spin size="large" />}
        </div>
      </Space>
    </div>
  )
}

export default TopupPackagePricing;